// FILE: PickerTriggerButton.tsx
// Purpose: Shares the trigger shell used by chat picker-style menus in the header and composer.
// Layer: Chat shell controls
// Depends on: button primitives, shared picker text styles, and icon slots supplied by callers.

import { type ComponentProps, type ReactNode } from "react";
import { ChevronDownIcon } from "~/lib/icons";
import { cn } from "~/lib/utils";
import { Button } from "../ui/button";
import { COMPOSER_PICKER_TRIGGER_TEXT_CLASS_NAME } from "./composerPickerStyles";

export function PickerTriggerButton(
  props: {
    icon: ReactNode;
    label: ReactNode;
    compact?: boolean;
  } & Omit<ComponentProps<typeof Button>, "children" | "size" | "variant">,
) {
  const { icon, label, compact, className, ...buttonProps } = props;

  return (
    <Button
      {...buttonProps}
      size="sm"
      variant="ghost"
      className={cn(
        "min-w-0 justify-start overflow-hidden whitespace-nowrap px-1.5 [&_svg]:mx-0",
        COMPOSER_PICKER_TRIGGER_TEXT_CLASS_NAME,
        compact ? "max-w-42 shrink-0" : "max-w-48 shrink sm:max-w-56 sm:px-1.5",
        className,
      )}
    >
      <span
        className={cn(
          "flex min-w-0 w-full items-center gap-2 overflow-hidden",
          compact ? "max-w-36" : undefined,
        )}
      >
        <span className="inline-flex size-3.5 shrink-0 items-center justify-center">{icon}</span>
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <ChevronDownIcon aria-hidden="true" className="size-3 shrink-0 opacity-60" />
      </span>
    </Button>
  );
}
